import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, AlertTriangle, Loader2, Gift, RotateCcw, Phone, Mail } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const prettyDate = (iso) => (iso ? new Date(iso.length > 10 ? iso : iso + "T00:00:00").toLocaleDateString("fr-FR") : "—");

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const ref = params.get("ref");
  const [order, setOrder] = useState(null);
  const [done, setDone] = useState(false);
  const attempts = useRef(0);

  const poll = useCallback(async () => {
    if (!ref) { setDone(true); return true; }
    try {
      const { data } = await api.get(`/public/orders/by-ref/${ref}/payment-status`);
      setOrder(data);
      if (["paid", "failed", "cancelled", "refunded"].includes(data.paymentStatus)) {
        setDone(true);
        return true;
      }
    } catch {
      // transient error: keep polling, do not show a false failure
    }
    return false;
  }, [ref]);

  useEffect(() => {
    let timer;
    const tick = async () => {
      attempts.current += 1;
      const finished = await poll();
      if (!finished && attempts.current < 20) timer = setTimeout(tick, 2000);
      else if (!finished) setDone(true); // exhausted → still-confirming screen
    };
    tick();
    return () => clearTimeout(timer);
  }, [poll]);

  const status = order?.paymentStatus;
  const pending = !done;

  const Wrap = ({ children }) => (
    <div className="min-h-screen bg-[#FDFBF7] bg-grain flex items-center justify-center px-5 py-16">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        data-testid="payment-return" data-payment-status={status || "loading"}
        className="max-w-lg w-full bg-white rounded-2xl border border-[#E5DEC9] shadow-lg p-8 text-center">
        {children}
      </motion.div>
    </div>
  );

  if (pending)
    return (
      <Wrap>
        <Loader2 className="w-12 h-12 animate-spin text-[#581825] mx-auto" />
        <h1 className="font-serif text-2xl font-bold text-[#581825] mt-6">Paiement en cours de confirmation…</h1>
        <p className="text-[#6E675F] mt-3">Merci de patienter, nous confirmons votre paiement auprès de la banque.</p>
      </Wrap>
    );

  if (status === "paid")
    return (
      <Wrap>
        <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
          <CheckCircle2 className="w-9 h-9 text-emerald-700" />
        </div>
        <h1 className="font-serif text-3xl font-bold text-[#581825] mt-6">Votre BOX VIN est réservée</h1>
        <p className="text-[#1A1818] mt-3">Merci {order.firstName} !</p>
        <div className="mt-6 text-left bg-[#F5EFEB] rounded-xl p-5 space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-[#6E675F]">Formule</span><span className="font-semibold text-[#581825]">{order.planLabel}</span></div>
          <div className="flex justify-between"><span className="text-[#6E675F]">Montant payé</span><span className="font-semibold text-[#110F0F]">{order.amount} €</span></div>
          <div className="flex justify-between"><span className="text-[#6E675F]">Récupération</span><span className="font-semibold text-[#110F0F]">{order.fulfillmentType === "livraison" ? "Livraison" : "Retrait magasin"}</span></div>
          <div className="flex justify-between"><span className="text-[#6E675F]">Date prévue</span><span className="font-semibold text-[#110F0F]">{prettyDate(order.scheduledDate)}</span></div>
        </div>
        <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#C5A059]/20 text-[#581825] px-4 py-2 text-sm font-semibold">
          <Gift className="w-4 h-4" /> La Cave Christophe vous recontacte très vite.
        </div>
        <Button onClick={() => navigate("/")} className="mt-8 bg-[#581825] hover:bg-[#42101A] text-[#FDFBF7] rounded-full px-8">Retour à l'accueil</Button>
      </Wrap>
    );

  if (status === "cancelled")
    return (
      <Wrap>
        <div className="mx-auto w-16 h-16 rounded-full bg-stone-200 flex items-center justify-center">
          <XCircle className="w-9 h-9 text-stone-600" />
        </div>
        <h1 className="font-serif text-2xl font-bold text-[#581825] mt-6">Paiement annulé</h1>
        <p className="text-[#6E675F] mt-3">Votre paiement a été annulé. Aucun montant n'a été débité.</p>
        <Button onClick={() => navigate("/")} className="mt-8 bg-[#581825] hover:bg-[#42101A] text-[#FDFBF7] rounded-full px-8">
          <RotateCcw className="w-4 h-4 mr-2" /> Recommencer
        </Button>
      </Wrap>
    );

  if (status === "refunded")
    return (
      <Wrap>
        <div className="mx-auto w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
          <RotateCcw className="w-9 h-9 text-blue-700" />
        </div>
        <h1 className="font-serif text-2xl font-bold text-[#581825] mt-6">Paiement remboursé</h1>
        <p className="text-[#6E675F] mt-3">Ce paiement a été remboursé. Contactez la Cave pour toute question.</p>
        <div className="mt-5 space-y-1 text-sm text-[#6E675F]">
          <p className="flex items-center justify-center gap-2"><Phone className="w-4 h-4 text-[#581825]" /> 06 07 15 59 70</p>
          <p className="flex items-center justify-center gap-2"><Mail className="w-4 h-4 text-[#581825]" /> caves.christophe@wanadoo.fr</p>
        </div>
        <Button onClick={() => navigate("/")} className="mt-8 bg-[#581825] hover:bg-[#42101A] text-[#FDFBF7] rounded-full px-8">Retour à l'accueil</Button>
      </Wrap>
    );

  if (status === "failed")
    return (
      <Wrap>
        <div className="mx-auto w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
          <AlertTriangle className="w-9 h-9 text-red-700" />
        </div>
        <h1 className="font-serif text-2xl font-bold text-[#581825] mt-6">Le paiement n'a pas abouti</h1>
        <p className="text-[#6E675F] mt-3">Le paiement a été refusé. Vous pouvez réessayer.</p>
        <Button onClick={() => navigate("/")} className="mt-8 bg-[#581825] hover:bg-[#42101A] text-[#FDFBF7] rounded-full px-8">
          <RotateCcw className="w-4 h-4 mr-2" /> Réessayer
        </Button>
      </Wrap>
    );

  // Still confirming (poll exhausted / unknown) — never claim failure on a maybe-successful payment.
  return (
    <Wrap>
      <Loader2 className="w-12 h-12 animate-spin text-[#581825] mx-auto" />
      <h1 className="font-serif text-2xl font-bold text-[#581825] mt-6">Confirmation en cours…</h1>
      <p className="text-[#6E675F] mt-3">
        Si vous avez validé le paiement, il sera confirmé dans un instant et vous recevrez un email.
        Inutile de payer à nouveau.
      </p>
      <div className="mt-5 space-y-1 text-sm text-[#6E675F]">
        <p className="flex items-center justify-center gap-2"><Phone className="w-4 h-4 text-[#581825]" /> 06 07 15 59 70</p>
        <p className="flex items-center justify-center gap-2"><Mail className="w-4 h-4 text-[#581825]" /> caves.christophe@wanadoo.fr</p>
      </div>
      <Button onClick={() => navigate("/")} className="mt-8 bg-[#581825] hover:bg-[#42101A] text-[#FDFBF7] rounded-full px-8">
        Retour à l'accueil
      </Button>
    </Wrap>
  );
}
